import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import styled from 'styled-components';
import { motion, AnimatePresence } from 'framer-motion';
import { Menu, X } from 'lucide-react';
import contextjetLogo from '../assets/contextjet_logo.png';

const HeaderWrapper = styled(motion.header)`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  z-index: 1000;
  padding: 1rem 0;
`;

const HeaderContainer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.75rem 1.5rem;
  border-radius: 9999px;
  transition: all 0.3s ease;
  
  /* Conditional styling based on scroll state applied via framer-motion */
`;

const LogoContainer = styled(Link)`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  z-index: 1001; /* Keep above mobile menu */
`;

const LogoImage = styled.img`
  height: 2.2rem;
  width: auto;
`;

const LogoText = styled.span`
  font-size: 1.4rem;
  font-weight: 700;
  letter-spacing: -0.02em;
  color: var(--text-primary);
  
  @media (max-width: 380px) {
    display: none;
  }
`;

const Nav = styled.nav`
  display: flex;
  gap: 2rem;
  align-items: center;

  @media (max-width: 768px) {
    display: none;
  }
`;

const NavLink = styled(Link)`
  color: var(--text-secondary);
  font-weight: 500;
  font-size: 0.95rem;
  position: relative;
  transition: color 0.3s ease;

  &:hover {
    color: var(--text-primary);
  }

  &.active {
    color: var(--text-primary);
  }

  &::after {
    content: '';
    position: absolute;
    bottom: -4px;
    left: 0;
    width: 0;
    height: 2px;
    background: linear-gradient(90deg, var(--accent-primary), var(--accent-secondary));
    transition: width 0.3s ease;
    border-radius: 2px;
  }

  &:hover::after,
  &.active::after {
    width: 100%;
  }
`;

const MobileMenuButton = styled.button`
  display: none;
  background: none;
  color: var(--text-primary);
  z-index: 1001;

  @media (max-width: 768px) {
    display: flex;
    align-items: center;
    justify-content: center;
  }
`;

const MobileNavMenu = styled(motion.div)`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: var(--bg-primary);
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  gap: 2rem;
  z-index: 1000;
  padding: 2rem;
`;

const MobileNavLink = styled(Link)`
  font-size: 2rem;
  font-weight: 600;
  color: var(--text-secondary);
  
  &.active {
    color: var(--text-primary);
    background: linear-gradient(135deg, var(--text-primary) 0%, var(--text-secondary) 100%);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
  }
`;

const Header = () => {
  const [scrolled, setScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Prevent scrolling when mobile menu is open
  useEffect(() => {
    if (mobileMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
  }, [mobileMenuOpen]);

  const navItems = [
    { path: '/', label: 'Home' },
    { path: '/solutions', label: 'Solutions' },
    { path: '/case-studies', label: 'Case Studies' },
    { path: '/about', label: 'About' },
    { path: '/blog', label: 'Blog' },
    { path: '/contact', label: 'Contact' }
  ];

  return (
    <>
      <HeaderWrapper>
        <div className="container" style={{ maxWidth: '1100px' }}>
          <motion.div
            initial={false}
            animate={{
              backgroundColor: scrolled ? 'rgba(5, 5, 5, 0.7)' : 'rgba(5, 5, 5, 0)',
              backdropFilter: scrolled ? 'blur(16px)' : 'blur(0px)',
              border: scrolled ? '1px solid rgba(255, 255, 255, 0.08)' : '1px solid rgba(255, 255, 255, 0)',
              boxShadow: scrolled ? '0 10px 30px -10px rgba(0, 0, 0, 0.5)' : 'none',
              paddingY: scrolled ? '0.75rem' : '1.5rem',
            }}
            transition={{ duration: 0.3 }}
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              padding: '0.75rem 2rem',
              borderRadius: '9999px',
            }}
          >
            <LogoContainer to="/" onClick={() => setMobileMenuOpen(false)}>
              <LogoImage src={contextjetLogo} alt="ContextJet.Ai Logo" />
              <LogoText>ContextJet.Ai</LogoText>
            </LogoContainer>

            <Nav>
              {navItems.map(item => (
                <NavLink
                  key={item.path}
                  to={item.path}
                  className={location.pathname === item.path ? 'active' : ''}
                >
                  {item.label}
                </NavLink>
              ))}
              <Link to="/contact" className="glow-btn" style={{ marginLeft: '1rem', padding: '0.6rem 1.5rem' }}>
                Get Started
              </Link>
            </Nav>

            <MobileMenuButton onClick={() => setMobileMenuOpen(!mobileMenuOpen)}>
              {mobileMenuOpen ? <X size={28} /> : <Menu size={28} />}
            </MobileMenuButton>
          </motion.div>
        </div>
      </HeaderWrapper>

      <AnimatePresence>
        {mobileMenuOpen && (
          <MobileNavMenu
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ duration: 0.2 }}
          >
            {navItems.map((item, i) => (
              <motion.div
                key={item.path}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.1 }}
              >
                <MobileNavLink
                  to={item.path}
                  className={location.pathname === item.path ? 'active' : ''}
                  onClick={() => setMobileMenuOpen(false)}
                >
                  {item.label}
                </MobileNavLink>
              </motion.div>
            ))}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: navItems.length * 0.1 }}
              style={{ marginTop: '2rem' }}
            >
              <Link
                to="/contact"
                className="glow-btn"
                onClick={() => setMobileMenuOpen(false)}
                style={{ display: 'inline-block', fontSize: '1.2rem', padding: '1rem 3rem' }}
              >
                Get Started
              </Link>
            </motion.div>
          </MobileNavMenu>
        )}
      </AnimatePresence>
    </>
  );
};

export default Header;